import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import {
    Typography,
    FormControl,
    Pagination,
    IconButton,
    Tooltip,
    Modal,
    Box,
    Button,
    Card,
    Alert,
    CardContent,
    Dialog, DialogTitle, DialogContent, DialogActions, ialogActions, TextField, MenuItem, Select, InputLabel
} from "@mui/material";
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import LoadingButton from '@mui/lab/LoadingButton';
import LinearProgress from '@mui/material/LinearProgress';
import SearchIcon from '@mui/icons-material/Search';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import Autocomplete from '@mui/material/Autocomplete';


import {
    DataGrid,
    GridToolbarContainer
} from '@mui/x-data-grid';
import { fetchManifest, resetGenerateManifestNotification, dopostTrackingNumberCloseManifest, doRequestManifestPickup } from './manifestsSlice';
import {
    getPickupSettings,
    postPickupSettings,
    resetPickupSettingsState,
  } from "../../../pages/Settings/PickupSettings/pickupSettingsSlice";
//Custom Components Import Starts
import Carrier from '../../General/Carrier'
import NorowsData from '../../General/NorowsData'
import Search from './Search'
import Notification from '../../../pages/General/Notification'
import ManifestDrawer from '../../../pages/General/ManifestDrawer'
import Printer from '../../../pages/General/Printer'
import UnclosedShipmentDrawer from '../../../pages/General/UnclosedShipmentDrawer'
import PendingShipmentsDrawer from '../../../pages/General/PendingShipmentsDrawer'
import { fetchSenderAddress } from '../../../pages/Settings/AddressSender/addressSenderSlice';

import CustomDate from '../../../pages/General/Date'

//Custom Components Import Ends
function PickupModal({ 
    open, 
    onClose, 
    addresses, 
    selectedAddress, 
    setSelectedAddress, 
    selectedDate, // New state from parent
    setSelectedDate, 
    selectedSlot, 
    setSelectedSlot,
    handlePickup,
    loading 
}) {
    
    // Helper to get next 5 working days (Mon-Fri)
    const getAvailableDates = () => {
        const dates = [];
        let current = new Date();
        
        // Let's get the next 7 calendar days and filter for Mon-Fri
        for (let i = 1; i <= 7; i++) {
            let nextDay = new Date();
            nextDay.setDate(current.getDate() + i);
            let dayOfWeek = nextDay.getDay();
            
            if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Skip Sat (6) and Sun (0)
                const label = i === 1 ? `Tomorrow (${nextDay.toLocaleDateString()})` : nextDay.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
                dates.push({ label, value: nextDay.toISOString().split('T')[0] });
            }
            if (dates.length >= 5) break; // Limit to 5 options
        }
        return dates;
    };

    const timeSlots = [
        "9am to 1pm",
        "1pm to 5pm"
    ];

    const availableDates = getAvailableDates();

    return (
        <Dialog open={open} onClose={onClose} fullWidth>
            <DialogTitle>Schedule Pickup Request</DialogTitle>
            <DialogContent style={{ paddingTop: '10px' }}>
                {/* Address Selection */}
                <Autocomplete
                    options={addresses}
                    value={selectedAddress || null}
                    onChange={(e, newValue) => setSelectedAddress(newValue)}
                    // 1. This controls the text inside the input field after selection
                    getOptionLabel={(option) =>
                        option ? `${option.sender_name} - ${option.sender_address}, ${option.sender_city}, ${option.sender_state_code} ${option.sender_postal_code}` : ""
                    }
                    isOptionEqualToValue={(option, value) => option._id === value._id}
                    renderOption={(props, option) => (
                        <li {...props} key={option._id}>
                            <div style={{ width: '100%' }}>
                                <strong>{option.sender_name}</strong> {option.sender_company ? `(${option.sender_company})` : ""}
                                <div style={{ fontSize: '0.85em', color: '#555', marginTop: '2px' }}>
                                    {option.sender_address}, {option.sender_city}, {option.sender_state_code} {option.sender_postal_code}, {option.sender_country_code}
                                </div>
                            </div>
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField 
                            {...params} 
                            label="Pickup Address" 
                            variant="outlined" 
                            margin="dense"
                            helperText={selectedAddress ? "Selected address will be used for pickup" : "Search or select a sender address"} 
                        />
                    )}
                />

                {/* Date Selection */}
                <FormControl fullWidth style={{ marginTop: '20px' }}>
                    <InputLabel>Select Pickup Date</InputLabel>
                    <Select
                        value={selectedDate}
                        label="Select Pickup Date"
                        onChange={(e) => setSelectedDate(e.target.value)}
                    >
                        {availableDates.map((date) => (
                            <MenuItem key={date.value} value={date.value}>{date.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Time Selection */}
                <FormControl fullWidth style={{ marginTop: '20px' }}>
                    <InputLabel>Select Time Slot</InputLabel>
                    <Select
                        value={selectedSlot}
                        label="Select Time Slot"
                        onChange={(e) => setSelectedSlot(e.target.value)}
                    >
                        {timeSlots.map(slot => (
                            <MenuItem key={slot} value={slot}>{slot}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>Cancel</Button>
                <LoadingButton
                    variant="contained"
                    color="primary"
                    loading={loading}
                    disabled={!selectedAddress || !selectedSlot || !selectedDate}
                    onClick={handlePickup}
                >
                    Confirm Pickup
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}

const ManifestsPage = () => {

    const dispatch = useDispatch();
    const manifest = useSelector(state => state.manifests);
    const profile = useSelector(state => state.auth.profile);
    const pickupsettings = useSelector((state) => state.pickupsettings);
    const addresssender = useSelector(state => state.addresssender);
    const [meta, setMeta] = useState(manifest.meta);
    const [showFilter, setShowFilter] = useState(false);
    const [showSearch, setShowSearch] = useState(false);
    const [manifestId, setManifestId] = useState("");
    const [manifestUrl, setManifestUrl] = useState("");
    const [refreshIntervalId, setRefreshIntervalId] = useState("")
    const [manifestDrawerVisible, setManifestDrawerVisible] = useState(false);
    const [openAllManifestCloseConfirmModal, setOpenAllManifestCloseConfirmModal] = useState(false)
    const [unclosedShipmentsDrawerVisible, setUnclosedShipmentsDrawerVisible] = useState(false)
    const [pendingShipmentsDrawerVisible, setPendingShipmentsDrawerVisible] = useState(false)
    const [shipmentType, setShipmentType] = useState("");
    const [printUrl, setPrintUrl] = useState("")
    const [isPrinted, setIsPrinted] = useState(true)
    const [selectedManifests, setSelectedManifests] = useState([]);
    const [openPickupModal, setOpenPickupModal] = useState(false);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [selectedSlot, setSelectedSlot] = useState('');
    const [selectedDate, setSelectedDate] = useState('');
    const isPickupLoading = manifest.pickupLoading;
    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 600,
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
    };
    const manifestCallback = (manifest_number, url = "") => {
        setManifestId(manifest_number);
        setManifestUrl(url)
        setManifestDrawerVisible(true)
    }

    const manifestOnClose = (values) => {
        setManifestDrawerVisible(false);
    };

    const closeAllUnmanifestedShipments = (value) => {
        let pickup_preference=false;
        if(value=="nopickup"){
            var postData = {
                "close_all_unclosed_shipments": true,
                "ignore_errors": true,
                "request_medium":"UI",
                "subuser_id":profile.id
            }
        }else{
            if(value==true){
                pickup_preference=true
            }else if(value==false){
                pickup_preference=false
            }
            var postData = {
                "close_all_unclosed_shipments": true,
                "ignore_errors": true,
                "pickup_preference":pickup_preference,
                "request_medium":"UI",
                "subuser_id":profile.id
            }
        }
        dispatch(dopostTrackingNumberCloseManifest(profile.id,postData, profile.api_token, meta))
        setOpenAllManifestCloseConfirmModal(false);
    }

    const callUnclosedShipments = () => {
        setUnclosedShipmentsDrawerVisible(true);
    }

    const unclosedShipmentsOnClose = () => {
        setUnclosedShipmentsDrawerVisible(false);
    }

    const callPendingShipments = (manifest_number, shipment_type) => {
        setManifestId(manifest_number);
        setShipmentType(shipment_type);
        setPendingShipmentsDrawerVisible(true);
    }

    const pendingShipmentsOnClose = () => {
        setPendingShipmentsDrawerVisible(false);
    }

    const columns = [
        {
            field: 'manifest_number',
            headerName: 'Manifest Number',
            headerClassName: 'super-app-theme--header',
            sortable: false,
            minWidth: 200,
            renderCell: (params) => {
                return <span style={{ color: 'rgb(24, 144, 255)', cursor: 'pointer' }} onClick={e => { manifestCallback(params.row.manifest_number, params.row && params.row.carrier_manifest_url && params.row.carrier_manifest_url) }}>{params.row.manifest_number}</span>

            }
        },
        {
            field: 'status',
            headerName: 'Status',
            headerClassName: 'super-app-theme--header',
            minWidth: 200,
            renderCell: (params) => {
                if (params.row.status) {
                    return params.row.status
                } else {
                    return "-"
                }
            }
        },
       
        // {
        //     field: 'total_shipments',
        //     headerName: 'Manifested Shipments',
        //     headerClassName: 'super-app-theme--header',
        //     flex: 1,
        //     renderCell: (params) =>
        //     {
        //         if(params.row.total_shipments !== 0){
        //             return <div> <a href='#' onClick={() => { callPendingShipments(params.row.manifest_number, "total_shipments") }} >{params.row.total_shipments} </a></div>
        //         }else{
        //             return <div><span>{params.row.total_shipments}</span></div>
        //         }
        //     }
        // },
        // {
        //     field: 'received_shipments',
        //     headerName: 'Received Shipments',
        //     headerClassName: 'super-app-theme--header',
        //     flex: 1,
        //     renderCell: (params) =>
        //     {
        //         if(params.row.received_shipments !== 0){
        //             return <div> <a href='#' onClick={() => { callPendingShipments(params.row.manifest_number, "received_shipments") }} >{params.row.received_shipments} </a></div>
        //         }else{
        //             return <div><span>{params.row.received_shipments}</span></div>
        //         }
        //     }
        // },
        // {
        //     field: 'not_yet_received_shipments',
        //     headerName: 'Not Yet Received Shipments',
        //     headerClassName: 'super-app-theme--header',
        //     flex: 1,
        //     renderCell: (params) =>
        //     {
        //         if(params.row.not_yet_received_shipments !== 0){
        //             return <div> <a href='#' onClick={() => { callPendingShipments(params.row.manifest_number, "not_yet_received_shipments") }} >{params.row.not_yet_received_shipments} </a></div>
        //         }else{
        //             return <div><span>{params.row.not_yet_received_shipments}</span></div>
        //         }
        //     }
        // },
        {
            field: 'created_at',
            headerName: 'Closed Date',
            headerClassName: 'super-app-theme--header',
            minWidth: 200,
            renderCell: (params) => {
                return <CustomDate date={params.row.created_at} />
            }
        },
        {
            field: 'action',
            headerName: 'Manifest Action',
            sortable: false,
            minWidth: 200,
            renderCell: (params) => {
            const isDisabled = !params.row.manifest_url;
            const isPickupRequested = !!params.row.pickup_enabled;
            return (
                <div>
                <span style={{ marginRight: '10px' }}>
                    <Tooltip title="View" placement="left-start">
                    <span>
                        <IconButton
                        disabled={isDisabled}
                        onClick={() => window.open(params.row.manifest_url, '_blank')}
                        >
                        <RemoveRedEyeIcon style={{ color: "#1890ff", cursor: isDisabled ? 'not-allowed' : 'pointer' }} />
                        </IconButton>
                    </span>
                    </Tooltip>
                </span>

                <span style={{ marginRight: '10px' }}>
                    <Tooltip title="Print" placement="left-start">
                    <span>
                        <IconButton
                        disabled={isDisabled}
                        onClick={() => {
                            setPrintUrl(params.row.manifest_url);
                            setIsPrinted(false);
                        }}
                        >
                        <LocalPrintshopIcon style={{ color: "#1890ff", cursor: isDisabled ? 'not-allowed' : 'pointer' }} />
                        </IconButton>
                    </span>
                    </Tooltip>
                </span>
                {profile?.enable_pickup_manifest && (
                    <span style={{ marginRight: '10px' }}>
                        <Tooltip title={isPickupRequested ? "Pickup already requested" : "Pickup Request"}>
                            <span>
                                <IconButton
                                    disabled={isDisabled || params.row.status !== 'CLOSED' || isPickupRequested}
                                    onClick={() => handleSinglePickupOpen(params.row.manifest_number)}
                                >
                                    <LocalShippingIcon
                                        style={{
                                            color: (isDisabled || params.row.status !== 'CLOSED' || isPickupRequested) 
                                                    ? "#d9d9d9" 
                                                    : "#1890ff",
                                            cursor: (isDisabled || params.row.status !== 'CLOSED' || isPickupRequested)
                                                    ? "not-allowed"
                                                    : "pointer"
                                        }}
                                    />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </span>
                )}
                </div>
            );
            }

        }
    ];

    const onSortChange = (currentSorting) => {
        setMeta({
            ...meta,
            ...{
                sorting: JSON.stringify(currentSorting)
            }
        });
    }

    const onChangePerPage = (event) => {
        setMeta({
            ...meta,
            ...{
                page: 1,
                per_page: event.target.value
            }
        });
    }

    const onChangePagination = (event, page) => {
        setMeta({
            ...meta,
            ...{
                page: page
            }
        });
    }

    const onMenuClick = (type, show) => {
        setShowSearch((type === "search" && show === true) ? true : false)
        setShowFilter((type === "filter" && show === true) ? true : false)
    }

    function CustomTotalRows() {

        return (
            <div className="custom-total-rows">
                <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
                    Showing {manifest && manifest.data && manifest.data.length} of {(manifest && manifest.meta && manifest.meta.total) ? manifest.meta.total : 0}
                </FormControl>
            </div>
        )
    }


    function CustomPagesizeoptions() {

        return (
            <div className="custom-page-size-options">
                <span>
                    Show <select
                        value={meta && meta.per_page}
                        onChange={onChangePerPage}
                    >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select> entries
                </span>
            </div>
        )
    }

    function CustomToolbar() {

        return (
            <GridToolbarContainer>
                <CustomPagesizeoptions />
                <CustomTotalRows />
            </GridToolbarContainer>
        );
    }

    function CustomPagination() {

        return (
            <Pagination
                color="primary"
                shape="circular"
                page={manifest && manifest.meta && manifest.meta.current_page}
                count={manifest && manifest.meta && manifest.meta.last_page}
                onChange={onChangePagination}
            />
        );
    }

    useEffect(() => {
        setMeta({
            ...meta,
            ...{
                shippingchimp_manifest_number: "",
            }
        });
    }, [dispatch])

    useEffect(() => {
        dispatch(fetchSenderAddress());
        dispatch(fetchManifest(meta))
    }, [meta]);

    useEffect(() => {
        if ((manifest && manifest.un_closed_shipments_count != 0 && manifest.manifest_all_in_progress == true) || (manifest && manifest.manifest_all_in_progress == true)) {
            if (refreshIntervalId == "") {
                let intervalId = setInterval(() => {
                    dispatch(fetchManifest(meta))
                }, 15000);
                setRefreshIntervalId(intervalId)
            }
        } else {
            if (manifest && manifest.manifest_all_in_progress == false && refreshIntervalId != "") {
                clearInterval(refreshIntervalId)
            }
        }
    }, [manifest.un_closed_shipments_count, manifest.manifest_all_in_progress]);

    useEffect(() => {
        dispatch(getPickupSettings());
      }, []);

    
   useEffect(() => {
        if (addresssender?.data?.length > 0) {
            const defaultAddress =
            addresssender.data.find(a => a.default) || addresssender.data[0];
            setSelectedAddress(defaultAddress);
        }
    }, [addresssender]); // ✅ only runs when API data changes

    // Reset state when modal closes
    const handleClosePickupModal = () => {
        setSelectedManifests([])
        setOpenPickupModal(false);
        setSelectedSlot(""); // clear slot
        setSelectedDate(""); // Clear date
        if (addresssender?.data?.length > 0) {
            const defaultAddress =
            addresssender.data.find(a => a.default) || addresssender.data[0];
            setSelectedAddress(defaultAddress); // reset to default
        } else {
            setSelectedAddress(null);
        }
    };
 

// Modified Single Pickup Handler (Truck Icon)
const handleSinglePickupOpen = (manifestNumber) => {
    setSelectedManifests([manifestNumber]); // Put single ID in the array
    setOpenPickupModal(true);
};


// The actual API Dispatch logic
const handlePickup = () => {
    const payload = {
        manifest_numbers: selectedManifests, // This works for both single and bulk now
        pickup_details: {
            name: selectedAddress.sender_name,
            company: selectedAddress.sender_company,
            address: selectedAddress.sender_address,
            city: selectedAddress.sender_city,
            state: selectedAddress.sender_state_code,
            postal_code: selectedAddress.sender_postal_code,
            country: selectedAddress.sender_country_code,
            phone: selectedAddress.sender_phone
        },
        time_slot: selectedSlot,
        pickup_date: selectedDate 
    };

    console.log("Final Payload:", payload);
    dispatch(doRequestManifestPickup(payload, profile.api_token, profile.id, meta));
    setSelectedManifests([])
    handleClosePickupModal();
};


    return (
        <>
            { (manifest && manifest.notification && manifest.notification.show) &&
                <Notification
                    is_open={manifest.notification.show}
                    messageType={manifest.notification.messageType}
                    messages={manifest.notification.messageContent}
                    handleClose={() => dispatch(resetGenerateManifestNotification())}
                />}
            {(manifest && manifest.manifestClose.notification && manifest.manifestClose.notification.show) &&
                <Notification
                    is_open={manifest.manifestClose.notification.show}
                    messageType={manifest.manifestClose.notification.messageType}
                    messages={manifest.manifestClose.notification.messageContent}
                    handleClose={() => dispatch(resetGenerateManifestNotification())}
                />}
            <Printer isPrinted={isPrinted} setIsPrinted={setIsPrinted} print_url={printUrl} />
            <Modal
                open={openAllManifestCloseConfirmModal}
                aria-labelledby="manifest-confirm-modal-title"
                aria-describedby="manifest-confirm-modal-description"
            >
                <Box sx={style}>
                    <Typography style={{ textAlign: "center" }} id="manifest-confirm-modal-description" sx={{ mt: 2 }}>
                        This action will close manifest for ALL shipments. <br />Do you want to proceed?
                    </Typography>
                    { (pickupsettings &&
                    pickupsettings.data &&
                    pickupsettings.data.pickup_settings &&
                    pickupsettings.data.pickup_settings.pickup_preference &&
                    pickupsettings.data.pickup_settings.pickup_preference.pickup==true) && (pickupsettings &&
                    pickupsettings.data &&
                    pickupsettings.data.pickup_settings &&
                    pickupsettings.data.pickup_settings.pickup_preference &&
                    pickupsettings.data.pickup_settings.pickup_preference.dropoff==true) ?
                    <Typography style={{ marginTop: "30px", textAlign: "center" }}>
                        <Button variant="contained"  onClick={() => { closeAllUnmanifestedShipments(true) }}>Yes, Arrange pickup</Button>
                        <Button variant="contained" style={{ marginLeft: "15px" }} onClick={() => { closeAllUnmanifestedShipments(false) }}>Yes, i will drop off</Button>
                        <Button variant="outlined" style={{ marginLeft: "15px" }} onClick={() => { setOpenAllManifestCloseConfirmModal(false) }}>No</Button>
                    </Typography> :  <Typography style={{ marginTop: "30px", textAlign: "center" }}>
                        <Button variant="contained"  onClick={() => { closeAllUnmanifestedShipments("nopickup") }}>Yes</Button>
                        <Button variant="outlined" style={{ marginLeft: "15px" }} onClick={() => { setOpenAllManifestCloseConfirmModal(false) }}>No</Button>
                    </Typography>
                    }
                </Box>
            </Modal>
            <div>
                <div className="sccardhead">
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="h5" gutterBottom component="div">
                            Open Manifests
                    </Typography>
                    </div>
                </div>
                <Card sx={{ minWidth: 1000, boxShadow: 'unset', border: "1px solid rgba(224, 224, 224, 1)" }}>
                    <CardContent >
                        <Box sx={{ flexGrow: 1 }}>
                            <Grid container spacing={2} style={{ display: "flex", alignItems: "center" }}>
                                <Grid item xs={2} alignItems="center">
                                    <div>Total packages : {(manifest && manifest.un_closed_shipments_count && manifest.un_closed_shipments_count !== 0)
                                        ? <a href='#' onClick={() => { callUnclosedShipments() }} >{manifest.un_closed_shipments_count} </a> : <span>{manifest.un_closed_shipments_count}</span>}</div>
                                </Grid>
                                <Grid item xs={2}>
                                    <LoadingButton
                                        variant="contained"
                                        style={{ marginLeft: "20px" }}
                                        loading={manifest && manifest.manifestClose && manifest.manifestClose.loading}
                                        disabled={
                                            (manifest && manifest.manifestClose && manifest.manifestClose.loading) ||
                                            manifest.manifest_all_in_progress ||
                                            manifest.un_closed_shipments_count == 0
                                        }
                                        onClick={() => setOpenAllManifestCloseConfirmModal(true)}
                                    >
                                        {(manifest.manifest_all_in_progress == true) ? "Closing in progress" : "Close All Manifest"}
                                    </LoadingButton>
                                </Grid>
                                <Grid item xs={6}>
                                    <div>
                                        {(manifest.manifest_all_in_progress == true) && <Alert severity="warning">Please wait, closing manifest for all the shipments. Page will auto refresh until the process is completed</Alert>}
                                    </div>
                                </Grid>
                            </Grid>
                        </Box>
                    </CardContent>
                </Card>
            </div>
            <div className="scmaincard">
                <div className="sccardhead">
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="h5" gutterBottom component="div">
                            Closed Manifests
                    </Typography>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <ul className="features-list">
                            <li>
                                <Tooltip title="Seacrh" placement="top-start">
                                    <IconButton style={(showSearch) ? { backgroundColor: 'rgb(230, 230, 230)' } : {}} onClick={() => onMenuClick("search", !showSearch)}>
                                        <SearchIcon style={{ color: 'rgb(25, 118, 210)' }} />
                                    </IconButton>
                                </Tooltip>
                            </li>
                            <li>
                                {profile?.enable_pickup_manifest && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        disabled={selectedManifests.length === 0}
                                        onClick={() => {
                                        console.log("Bulk selected manifests:", selectedManifests);
                                        setOpenPickupModal(true)
                                        }}
                                        >
                                        Bulk Pickup Request
                                    </Button>
                                )}
                            </li>
                            {/* <li>
                                <Tooltip title="Filter" placement="top-start">
                                    <IconButton onClick={() => onMenuClick("filter", !showFilter)}>
                                        <FilterAltOutlinedIcon style={{ color: 'rgb(25, 118, 210)' }} />
                                    </IconButton>
                                </Tooltip>
                            </li> */}
                        </ul>
                    </div>
                </div>
                <Search visible={showSearch} meta={meta} setMeta={setMeta} />
                <div style={{ width: '100%', background: '#fff', marginTop: "20px" }}>
                    <DataGrid
                        style={{ paddingTop: "37px" }}
                        getRowId={(row) => row._id}
                        rows={manifest && manifest.data}
                        columns={columns}
                        autoHeight={true}
                        disableColumnFilter={true}
                        disableColumnMenu={true}
                        sortingMode="server"
                        onSortModelChange={(newSortModel) => onSortChange(newSortModel)}
                        sortModel={meta && meta.sorting && JSON.parse(meta.sorting)}
                        disableRowSelectionOnClick
                        slots={{
                            loadingOverlay: LinearProgress,
                            pagination: CustomPagination,
                            toolbar: CustomToolbar,
                            NorowsData
                        }}
                        loading={
                            (manifest && manifest.loading)
                        }
                        checkboxSelection={!!profile?.enable_pickup_manifest}
                        isRowSelectable={(params) => 
                            params.row.status === 'CLOSED' && 
                            !params.row.pickup_enabled && 
                            !!profile?.enable_pickup_manifest
                        }
                        onRowSelectionModelChange={(newSelection) => {
                            const selectedManifestNumbers = (manifest?.data || [])
                                .filter(row => newSelection.includes(row._id) && !row.pickup_enabled)
                                .map(row => row.manifest_number);

                            setSelectedManifests(selectedManifestNumbers);
                        }}
                    />
                </div>

            </div>
            {manifestId &&
                <ManifestDrawer
                    visible={manifestDrawerVisible} manifestId={manifestId} manifestOnClose={manifestOnClose} manifestUrl={manifestUrl}
                />
            }
            {manifestId && pendingShipmentsDrawerVisible && shipmentType!="" &&
                <PendingShipmentsDrawer
                    visible={pendingShipmentsDrawerVisible} manifestId={manifestId} pendingShipmentsOnClose={pendingShipmentsOnClose} shipmentType={shipmentType}
                />
            }
            {manifest && manifest.un_closed_shipments_count != 0 && unclosedShipmentsDrawerVisible &&
                <UnclosedShipmentDrawer
                    visible={unclosedShipmentsDrawerVisible} unclosedShipmentsOnClose={unclosedShipmentsOnClose}
                />
            }
            <PickupModal
                open={openPickupModal}
                onClose={handleClosePickupModal}
                addresses={addresssender.data || []}
                selectedAddress={selectedAddress}
                setSelectedAddress={setSelectedAddress}
                selectedDate={selectedDate}         // PASS VALUE
                setSelectedDate={setSelectedDate}
                selectedSlot={selectedSlot}
                setSelectedSlot={setSelectedSlot}
                handlePickup={handlePickup} // Added this
                loading={isPickupLoading} // Pass the loading state here
            />


        </>
    );
}

export default ManifestsPage;
